import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  ActionsCell,
  ClickableRow,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Segmented,
  StatusBadge,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { deleteAd, getAdsList } from "../../../services/ads";
import defaultAdImg from "../../../assets/images/default-ad.jpg";
import { matches } from "../../../utils/list";
import { canEditContent } from "../../../utils/roles";

const AdsList = () => {
  const canEdit = canEditContent("ads");
  const history = useHistory();
  const { t } = useTranslation();

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [adToDelete, setAdToDelete] = useState(null);

  useEffect(() => {
    getAdsList()
      .then((data) => setAds(data.adsList || []))
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      ads.filter(
        (ad) =>
          matches(ad?.title, search) &&
          (status === "all" || (status === "active") === Boolean(ad?.status))
      ),
    [ads, search, status]
  );
  const pagination = usePagination(filtered, 10, [search, status]);

  const handleDelete = async (ad_id) => {
    try {
      await deleteAd(ad_id);
      toast.success("Ad Deleted Successfully!", {
        progressStyle: { background: "var(--iq-primary)" },
      });
      setAds((previous) => previous.filter((ad) => ad._id !== ad_id));
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete movie!", {
        progressStyle: { background: "var(--iq-danger)" },
      });
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Advertisements")}
        subtitle={loading ? " " : `${ads.length} ${t("ads")}`}
        actions={
          canEdit && (
            <Link to="/dashboard/create-ads" className="ui-btn">
              <i className="ri-add-line" aria-hidden="true"></i>
              {t("Add Advertisement")}
            </Link>
          )
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search ad . . .")}
          />
          <Segmented
            label={t("status")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: t("All") },
              { value: "active", label: t("Active") },
              { value: "inactive", label: t("Inactive") },
            ]}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-coupon-3-line">
            {t("No Data Found")}
          </EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Advertisement")}</th>
                    <th>{t("Placement")}</th>
                    <th>{t("Expiry Date")}</th>
                    <th>{t("lang")}</th>
                    <th>{t("status")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((ad) => {
                    const placement = ad?.placement?.join(", ");
                    return (
                      <ClickableRow key={ad?._id} to={`/dashboard/ad-info/${ad?._id}`}>
                        <td>
                          <div className="ui-media">
                            <img
                              className="ui-media-thumb"
                              src={ad?.banner_url || defaultAdImg}
                              alt=""
                              loading="lazy"
                            />
                            <Link
                              to={`/dashboard/ad-info/${ad?._id}`}
                              className="ui-media-title"
                              title={ad?.title}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {ad?.title}
                            </Link>
                          </div>
                        </td>
                        <td>
                          <span className="ui-clamp" title={placement}>
                            {placement}
                          </span>
                        </td>
                        <td className="ui-cell-muted">
                          {ad?.expiry_date?.substring(0, 10)}
                        </td>
                        <td
                          className="ui-cell-muted"
                          style={{ textTransform: "uppercase" }}
                        >
                          {ad?.language_code}
                        </td>
                        <td>
                          <StatusBadge active={Boolean(ad?.status)} />
                        </td>
                        <ActionsCell>
                          <IconButton
                            icon="ri-eye-line"
                            label={t("View")}
                            onClick={() =>
                              history.push(`/dashboard/ad-info/${ad?._id}`)
                            }
                          />
                          {canEdit && (
                            <>
                              <IconButton
                                icon="ri-pencil-line"
                                label={t("Edit")}
                                onClick={() =>
                                  history.push(
                                    `/dashboard/update-ad/${ad?._id}`
                                  )
                                }
                              />
                              <IconButton
                                icon="ri-delete-bin-line"
                                label={t("Delete")}
                                tone="danger"
                                onClick={() => setAdToDelete(ad)}
                              />
                            </>
                          )}
                        </ActionsCell>
                      </ClickableRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {adToDelete && (
        <Popup
          open={true}
          setOpen={() => setAdToDelete(null)}
          deleteHandle={() => handleDelete(adToDelete._id)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")} {adToDelete?.title}?
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default AdsList;
